import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useCurrentPageStore } from '../stores/currentPageStore';
import { useMenuStore } from '../stores/menuStore';
import { menusAPI, projectConfigAPI } from '../services/api';
import {
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  LayoutGrid,
  Search,
} from 'lucide-react';
import Logo from '../components/Logo';
import Trail from '../components/shell/Trail';
import { SectionPagesContext, type OpenSectionPages, type SectionPage } from '../components/shell/SectionPages';
import PageSearch from '../components/shell/PageSearch';
import MessagesLink from '../components/messages/MessagesLink';
import { MESSAGES_PATH } from '../components/messages/messagesPath';
import { OPEN_SEARCH_EVENT } from '../components/shell/openSearch';
import { TopProgress } from '../components/loading/TopProgress';
import { usePageInClass } from '../components/loading/usePageInClass';
import { applyThemePalette, clearThemePalette } from '../utils/themeColors';
import { recordPageVisit } from '../utils/pageVisits';
import { SECTIONS, normalisePath, sectionByMenuCode } from '../navigation/catalog';
import { DASHBOARD_HOME_PATH, allowedDashTabs, dashTabFromSlug } from '../navigation/dashboards';
import { menuIcon } from '../navigation/menuIcon';
import {
  firstPagePath, holdsDashboard, homePath, isDashboardTabMenu, isHiddenMenu, menuCodes, menuForPath, pageMenuItems,
  type MenuRules,
} from '../navigation/menuTree';
import { titleForTrail, trailFor } from '../navigation/trail';

interface MenuItemFromDB {
  id: string;
  name: string;
  code: string;
  icon: string | null;
  path: string | null;
  parent_id: string | null;
  display_order: number;
  is_active: boolean;
}

interface DashboardLayoutProps {
  children: React.ReactNode;
}

type NavItem = {
  name: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: NavItem[];
  /** Address of the section's front page, for a top-level entry that has one. */
  indexPath?: string;
};

// The colour of the page canvas, decided here and nowhere else.
// The screens that are made of cards sit on a soft grey, which is what lets a white
// card stand out: the home screen, every dashboard under it (the built-in ones and the
// ones a team built), every section front page, search, and Messages (its two panes are
// cards). Every other page was designed on white and keeps it. The header, the trail row
// and the menus are white on both.
const CARD_SCREENS: ReadonlySet<string> = new Set([
  DASHBOARD_HOME_PATH,
  '/search',
  MESSAGES_PATH,
  ...SECTIONS.flatMap((s) => (s.indexPath ? [s.indexPath] : [])),
]);
// A dashboard is any address under the home screen's own: /dashboard/<name> and
// /dashboard/custom/<id>.
const DASHBOARDS_UNDER = `${DASHBOARD_HOME_PATH}/`;
const canvasClass = (pathname: string): string => {
  const path = normalisePath(pathname);
  return CARD_SCREENS.has(path) || path.startsWith(DASHBOARDS_UNDER) ? 'bg-slate-50' : 'bg-white';
};

// The widths of the five placeholders drawn in the top bar while the menu is on its way.
// Uneven, like the names they stand in for.
const MENU_PLACEHOLDER_WIDTHS = ['w-24', 'w-28', 'w-20', 'w-28', 'w-16'] as const;

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, clearAuth, setPermissions, projectConfig, selectedProjectId, setProjectConfig, setSelectedProjectId, projectListVersion } = useAuthStore();
  // The menu is fetched here, once, and shared: the home screen, the section front
  // pages and search read this copy instead of asking the server again.
  const setMenu = useMenuStore((s) => s.setMenu);
  const clearMenu = useMenuStore((s) => s.clearMenu);
  // True once the menu answer (or its failure) has arrived, whatever it held.
  const menuAnswered = useMenuStore((s) => s.loaded);
  // The page fades in on arrival. A class on <main>, never a key: pages keep their state.
  // Every dashboard is one page that stays on screen while its content changes, so going
  // from one dashboard to another is not an arrival and does not fade what is already there.
  const pageInClass = usePageInClass(
    location.pathname.startsWith(`${DASHBOARD_HOME_PATH}/`) ? `${DASHBOARD_HOME_PATH}/*` : location.pathname,
  );

  // Resolve blob path or URL to a displayable image src via the backend proxy
  const resolveLogoUrl = (logoUrl: string | null | undefined): string | null => {
    if (!logoUrl) return null;
    // Azure Blob URLs require auth — extract the blob path and proxy through backend
    const azureBlobMatch = logoUrl.match(/blob\.core\.windows\.net\/[^/]+\/(.+)$/);
    if (azureBlobMatch) {
      return `/api/images/view/${encodeURI(azureBlobMatch[1])}`;
    }
    if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) return logoUrl;
    if (logoUrl.startsWith('/api/')) return logoUrl;
    const blobPath = logoUrl.replace(/^\/+/, '');
    return `/api/images/view/${encodeURI(blobPath)}`;
  };

  const isSuperAdmin = user?.adminRole === 'super_admin';
  const isAdmin = user?.adminRole === 'admin';
  const isAnyAdmin = isSuperAdmin || isAdmin;
  // External users with access to >1 project also need the switcher (same UX
  // as admins, gated by their accessibleProjects rather than admin role).
  const isMultiProjectExternalUser = !!user?.isExternalUser && (user?.accessibleProjects?.length ?? 0) > 1;
  const showProjectSwitcher = isAnyAdmin || isMultiProjectExternalUser;

  // All users see project-specific branding when a project is selected
  const projectLogoSrc = resolveLogoUrl(projectConfig?.logo_url);
  const projectDisplayTitle = projectConfig?.display_title || projectConfig?.project_name || null;

  // Apply a single consistent theme color across all projects
  const THEME_COLOR = '#3D5A80';
  useEffect(() => {
    applyThemePalette(THEME_COLOR);
    return () => clearThemePalette();
  }, []);

  // Dynamically update browser favicon and page title from project config.
  // Favicon uses ONLY the project's favicon_url — never the Project Logo.
  // Admins who want a browser tab icon must upload the dedicated Favicon / App Icon.
  // If no favicon is uploaded, fall back to the default ChannelPlex /logo.svg.
  useEffect(() => {
    const favicon = document.getElementById('app-favicon') as HTMLLinkElement | null;
    const appleIcon = document.getElementById('app-apple-icon') as HTMLLinkElement | null;
    const faviconUrl = resolveLogoUrl(projectConfig?.favicon_url);
    if (faviconUrl) {
      const img = new Image();
      img.onload = () => {
        if (favicon) favicon.href = faviconUrl;
        if (appleIcon) appleIcon.href = faviconUrl;
      };
      img.onerror = () => {
        if (favicon) favicon.href = '/logo.svg';
        if (appleIcon) appleIcon.href = '/logo.svg';
      };
      img.src = faviconUrl;
    } else {
      if (favicon) favicon.href = '/logo.svg';
      if (appleIcon) appleIcon.href = '/logo.svg';
    }
    document.title = 'ChannelPlex';
  }, [projectConfig]);

  // Re-fetch project config when selectedProjectId changes (e.g. admin switches project)
  useEffect(() => {
    const projectId = selectedProjectId || user?.accessibleProjects?.[0]?.projectId;
    if (!projectId) return;
    // Skip if config already matches this project
    if (projectConfig?.project_id === projectId) return;
    projectConfigAPI.getPublicConfig(projectId)
      .then((res) => {
        if (res.data?.data) {
          setProjectConfig(res.data.data);
        } else {
          // No config for this project — clear stale branding
          setProjectConfig(null);
        }
      })
      .catch(() => {
        // Clear stale config so header doesn't show previous project's branding
        setProjectConfig(null);
      });
  }, [selectedProjectId]);

  // ── Super Admin global project selector ──────────────────────────────────
  const [projects, setProjects] = useState<{ projectId: number; projectName: string; projectCode: string }[]>([]);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const projectDropdownRef = useRef<HTMLDivElement>(null);

  const filteredProjects = useMemo(
    () =>
      projectSearch.trim()
        ? projects.filter(
            (p) =>
              String(p.projectName ?? '').toLowerCase().includes(projectSearch.toLowerCase()) ||
              String(p.projectCode ?? '').toLowerCase().includes(projectSearch.toLowerCase())
          )
        : projects,
    [projects, projectSearch]
  );

  // Fetch project list — only show projects configured via Project Management screen
  // Super admin: all configured projects; Admin: only their assigned projects
  useEffect(() => {
    projectConfigAPI.getAll()
      .then((res) => {
        const configs: any[] = res.data?.data || [];
        const activeConfigs = configs.filter((c: any) => c.is_active !== false);
        const configuredIds = new Set(activeConfigs.map((c: any) => c.project_id));

        if (isSuperAdmin) {
          const list = activeConfigs.map((c: any) => ({
            projectId: c.project_id,
            projectName: String(c.master_project_name || c.project_name || ''),
            projectCode: String(c.master_project_code || ''),
          }));
          setProjects(list);
        } else if (isAdmin && user?.adminProjectIds?.length) {
          // Admin users: only show projects they are assigned to
          const adminProjectSet = new Set(user.adminProjectIds);
          const list = activeConfigs
            .filter((c: any) => adminProjectSet.has(c.project_id))
            .map((c: any) => ({
              projectId: c.project_id,
              projectName: String(c.master_project_name || c.project_name || ''),
              projectCode: String(c.master_project_code || ''),
            }));
          setProjects(list);
        } else if (user?.accessibleProjects?.length) {
          setProjects(
            user.accessibleProjects
              .filter((p) => configuredIds.has(p.projectId))
              .map((p) => ({
                projectId: p.projectId,
                projectName: p.projectName,
                projectCode: '',
              }))
          );
        }
      })
      .catch((err) => console.warn('Failed to fetch configured projects for selector:', err));
  }, [isSuperAdmin, isAdmin, user?.accessibleProjects, user?.adminProjectIds, location.pathname, projectListVersion]);

  // Auto-select first project if none selected, or reset if selected project was removed
  useEffect(() => {
    if (projects.length === 0) return;
    const stillExists = selectedProjectId && projects.some((p) => p.projectId === selectedProjectId);
    if (!selectedProjectId || !stillExists) {
      const autoId = user?.adminProjectId || user?.accessibleProjects?.[0]?.projectId || projects[0]?.projectId;
      if (autoId) setSelectedProjectId(autoId);
    }
  }, [projects, selectedProjectId]);

  // Close project dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(e.target as Node)) {
        setProjectDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // The profile button at the end of the top bar: the person's name, their role and
  // Sign out sit behind it, so the bar itself carries only the picture. Opened by a
  // click, never on hover; closed by a click outside it and by Escape.
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!profileOpen) return;
    const onDown = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setProfileOpen(false);
      profileButtonRef.current?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [profileOpen]);

  const handleGlobalProjectSelect = (projectId: number) => {
    setSelectedProjectId(projectId);
    setProjectDropdownOpen(false);
    setProjectSearch('');
  };

  const selectedProjectName = useMemo(() => {
    if (!selectedProjectId) return null;
    // First check configured projects
    const p = projects.find((pr) => pr.projectId === selectedProjectId);
    if (p) return p.projectName;
    // Fallback to user's accessible projects (when no config exists yet)
    const ap = user?.accessibleProjects?.find((pr) => pr.projectId === selectedProjectId);
    return ap ? ap.projectName : null;
  }, [selectedProjectId, projects, user?.accessibleProjects]);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [menuItems, setMenuItems] = useState<MenuItemFromDB[]>([]);
  const [hasRoleAccess, setHasRoleAccess] = useState<boolean | null>(null);
  const [userRoleNames, setUserRoleNames] = useState<string[]>([]);
  // The section whose pages are listed in the phone menu. The desktop top bar lists none.
  const [openPhoneSection, setOpenPhoneSection] = useState<string | null>(null);

  const isHrefActive = (href: string) => {
    const [pathPart, queryPart] = href.split('?');
    if (!pathPart) return false;
    if (location.pathname !== pathPart) return false;
    if (!queryPart) return true;
    return location.search === `?${queryPart}`;
  };

  const getHrefActiveScore = (href: string): number => {
    const [pathPart, queryPart] = href.split('?');
    if (!pathPart) return 0;
    if (location.pathname !== pathPart) return 0;
    if (!queryPart) return 80;
    return location.search === `?${queryPart}` ? 100 : 0;
  };

  const getNavItemActiveScore = (item: NavItem): number => {
    let best = 0;
    if (item.href) {
      best = Math.max(best, getHrefActiveScore(item.href));
    }
    // Every dashboard has its own address under /dashboard; all of them are "Dashboard".
    if (item.href === DASHBOARD_HOME_PATH && location.pathname.startsWith(`${DASHBOARD_HOME_PATH}/`)) {
      best = Math.max(best, 80);
    }
    // A section's front page belongs to the section, though none of its pages is open.
    if (item.indexPath && normalisePath(location.pathname) === item.indexPath) {
      best = Math.max(best, 80);
    }
    if (item.children?.length) {
      for (const child of item.children) {
        const childScore = getNavItemActiveScore(child);
        if (childScore > 0) {
          best = Math.max(best, Math.max(1, childScore - 10));
        }
      }
    }
    return best;
  };

  const isNavItemActive = (item: NavItem): boolean => {
    if (item.href && isHrefActive(item.href)) return true;
    if (!item.children) return false;
    return item.children.some((c) => isNavItemActive(c));
  };


  // The shared menu is one person's access list, so it goes when the shell goes: a
  // session that expires leaves through the login page without passing handleLogout.
  // An answer still in flight at that moment must not put it back.
  const shellMounted = useRef(true);
  useEffect(() => {
    shellMounted.current = true;
    return () => {
      shellMounted.current = false;
      clearMenu();
    };
  }, [clearMenu]);
  const shareMenu = useCallback((rows: MenuItemFromDB[]) => {
    if (shellMounted.current) setMenu(rows);
  }, [setMenu]);

  // Fetch user's permitted menus from database
  const fetchMenus = useCallback(async () => {
    try {
      // Use user's employee code to fetch permitted menus
      // Priority: userCode (emp_code) > userName (may also be emp_code) > id
      const userId = user?.userCode?.toString() || user?.userName || user?.id;
      if (!userId) {
        console.error('No user ID available');
        setHasRoleAccess(false);
        return;
      }

      const response = await menusAPI.getUserMenus(userId);
      if (response.data.success) {
        const roles = response.data.roles;
        setHasRoleAccess(Array.isArray(roles) && roles.length > 0);
        const names = Array.isArray(roles)
          ? roles
              .map((r: any) => (typeof r === 'string' ? r : r?.name || r?.role_name || ''))
              .filter(Boolean)
          : [];
        setUserRoleNames(names);
        const activeMenus: MenuItemFromDB[] = response.data.data.filter((m: MenuItemFromDB) => m.is_active);
        setMenuItems(activeMenus);
        shareMenu(activeMenus);
        // Store permissions in auth store
        if (response.data.permissions) {
          setPermissions(response.data.permissions);
        }
      } else {
        // No menu came back. Say so, or the screens reading the shared menu wait for ever.
        shareMenu([]);
      }
    } catch (err) {
      console.error('Error fetching menus:', err);
      // Fallback: show only approvals menu
      setMenuItems([]);
      shareMenu([]);
      setHasRoleAccess(false);
    }
  }, [user, setPermissions, shareMenu]);

  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);

  // Redirect away from /dashboard if user doesn't have dashboard access
  useEffect(() => {
    if (hasRoleAccess === null || menuItems.length === 0) return;
    // Each dashboard has its own address under /dashboard; none of them is open to
    // someone without the Dashboard.
    const onDashboard = location.pathname === '/dashboard' || location.pathname === '/'
      || location.pathname.startsWith('/dashboard/');
    if (!onDashboard) return;
    if (holdsDashboard(menuItems)) return;

    // The first page they hold. The logo's way home (homePath) reads the same rule.
    const firstPath = firstPagePath(menuItems);
    if (firstPath) {
      navigate(firstPath, { replace: true });
    }
  }, [menuItems, hasRoleAccess, location.pathname, navigate]);

  // ── Where the person is: the trail, the tab's title, and which pages they open ──
  // One set of hiding rules, shared with the home screen, the front pages and search.
  const menuRules = useMemo<MenuRules>(
    () => ({ isSuperAdmin, selectedProjectId }),
    [isSuperAdmin, selectedProjectId],
  );
  const pages = useMemo(() => pageMenuItems(menuItems, menuRules), [menuItems, menuRules]);
  // The name of a dashboard someone built: the menu cannot supply it, the open page does.
  const currentPageTitle = useCurrentPageStore((s) => s.title);
  const trail = useMemo(
    () => trailFor(location.pathname, pages, menuRules, currentPageTitle),
    [location.pathname, pages, menuRules, currentPageTitle],
  );

  // The tab says which page it is. `projectConfig` is a dependency because the favicon
  // effect above puts the plain title back whenever the project's settings arrive.
  const pageTitle = titleForTrail(trail);
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle, projectConfig]);

  // Count page openings for "Pages you open most" (kept in this browser only), under
  // the same id the menu fetch uses. Counted once per arrival at an address: a tab or
  // filter change on the page, or the menu being fetched again, is not another opening.
  const visitKey = user?.userCode?.toString() || user?.userName || user?.id;
  const lastSeenPath = useRef<string | null>(null);
  useEffect(() => {
    // Until the menu is here, nobody knows whether this address is one of the person's pages.
    if (menuItems.length === 0) return;
    const path = location.pathname;
    if (lastSeenPath.current === path) return;
    lastSeenPath.current = path;
    // An old address is about to redirect; the opening is counted where it lands.
    if (normalisePath(path) !== path) return;
    const tab = path.startsWith(`${DASHBOARD_HOME_PATH}/`)
      ? dashTabFromSlug(path.slice(DASHBOARD_HOME_PATH.length + 1))
      : null;
    const isOwnDashboard = !!tab && holdsDashboard(menuItems) && allowedDashTabs(menuCodes(menuItems)).includes(tab);
    // The home screen is where the shortcuts are shown, so it is never one of them.
    const isMenuPage = path !== DASHBOARD_HOME_PATH && !!menuForPath(pages, path);
    if (isOwnDashboard || isMenuPage) recordPageVisit(visitKey, path);
  }, [location.pathname, menuItems, pages, visitKey]);

  // ── Search: the Search button, or "/" anywhere outside a text field or a pop-up ──
  const [searchOpen, setSearchOpen] = useState(false);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  // Where the keyboard was when "/" opened search. Closing puts it back there, so a
  // person part-way down a page does not have to Tab through it again.
  const focusBeforeSearch = useRef<HTMLElement | null>(null);
  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    const before = focusBeforeSearch.current;
    focusBeforeSearch.current = null;
    if (before && before !== document.body && before.isConnected) before.focus();
    // Opened from the Search button, or nothing to go back to: the Search button.
    if (!before || document.activeElement !== before) searchButtonRef.current?.focus();
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
      const el = e.target as HTMLElement | null;
      // In a text field "/" is just a character.
      if (el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName ?? '') || el.isContentEditable)) return;
      // A pop-up is open: search would open underneath it and take the keyboard out of it.
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      e.preventDefault();
      focusBeforeSearch.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setSearchOpen(true);
    };
    // A page may ask for search too (the bar on the home screen).
    const onAsk = () => {
      focusBeforeSearch.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setSearchOpen(true);
    };
    document.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_SEARCH_EVENT, onAsk);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_SEARCH_EVENT, onAsk);
    };
  }, []);

  const handleLogout = () => {
    clearAuth();
    clearMenu();
    navigate('/login');
    setShowLogoutConfirm(false);
  };


  if (hasRoleAccess === false) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-100 sticky top-0 z-30">
          <div className="w-full px-3 py-3 sm:px-4 sm:py-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Logo size="sm" logoUrl={projectLogoSrc} />
                <div className="hidden sm:flex flex-col leading-tight">
                  <span className="text-lg font-semibold text-gray-900">ChannelPlex</span>
                  <span className="text-[0.55rem] uppercase tracking-[0.35em] text-primary-500">Workforce Intelligence</span>
                </div>
              </div>

              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </div>
          </div>
        </header>

        <main className="w-full px-3 pt-3 pb-6 sm:px-4 sm:pt-4 sm:pb-6">
          <div className="min-h-[calc(100vh-140px)] flex items-center justify-center">
            <div className="text-center p-8 bg-yellow-50 border border-yellow-200 rounded-lg max-w-md">
              <h2 className="text-lg font-semibold text-yellow-800 mb-2">Unable to Load Menu</h2>
              <p className="text-sm text-yellow-700 mb-4">
                Could not connect to the server. Please check your network connection or try again.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm font-medium"
              >
                Retry
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const openLogoutDialog = () => setShowLogoutConfirm(true);
  const closeLogoutDialog = () => setShowLogoutConfirm(false);

  // Get settings menu and its children first (needed for navigation href)
  const settingsMenu = menuItems.find((m) => m.code === 'settings');
  const managementLinks = settingsMenu
    ? menuItems
        .filter((m) => m.parent_id === settingsMenu.id)
        // Menu Management, Admin Users and (for everyone but super admins) Project
        // Management: the rules live in navigation/menuTree.
        .filter((m) => !isHiddenMenu(m, menuRules))
        .sort((a, b) => a.display_order - b.display_order)
        .map((menu) => ({
          name: menu.name,
          icon: menuIcon(menu.icon),
          href: menu.path || '/settings',
        }))
    : [];

  // Build navigation from database menus (top-level menus only, excluding settings children)
  // For Settings menu, use the first submenu's path to avoid blank screen
  const firstSettingsSubmenuPath = managementLinks.length > 0 ? managementLinks[0].href : '/settings/roles';

  const tasksUpdatesChildren: NavItem[] = [];

  const navigation: NavItem[] = (() => {
    const visibleMenuItems = menuItems.filter((m) => {
      // Menu Management, Admin Users, Project Management for everyone but super
      // admins, and the Apple-only entries off project 170 (their pages gate
      // themselves; hiding them keeps the nav uncluttered). Rules: navigation/menuTree.
      if (isHiddenMenu(m, menuRules)) return false;
      // Dashboard tab children (dashboard_deployment/…/appeals) are per-tab access
      // tokens for Access Management + in-page gating (see DashboardPage). Keep them
      // OUT of the nav so "Dashboard" stays a single link (not a section with pages
      // under it); the getUserMenus ancestor walk still surfaces the Dashboard link
      // when a child is granted.
      if (isDashboardTabMenu(m)) return false;
      return true;
    });

    const childrenByParent = new Map<string | null, MenuItemFromDB[]>();
    for (const m of visibleMenuItems) {
      const key = m.parent_id ?? null;
      const list = childrenByParent.get(key) || [];
      list.push(m);
      childrenByParent.set(key, list);
    }

    const getMenuSortOrder = (m: MenuItemFromDB) => {
      const code = String(m.code || '').toLowerCase().trim();
      if (code === 'settings') return 9999;
      return m.display_order ?? 0;
    };

    for (const [, list] of childrenByParent.entries()) {
      list.sort((a, b) => {
        const order = getMenuSortOrder(a) - getMenuSortOrder(b);
        if (order !== 0) return order;
        return String(a.name).localeCompare(String(b.name));
      });
    }

    const isTasksUpdatesMenu = (_m: MenuItemFromDB) => {
      // Task Updates submenu should be filtered out - it's being removed
      return false;
    };

    const buildNavItem = (m: MenuItemFromDB): NavItem => {
      const rawChildren = childrenByParent.get(m.id) || [];
      const childNavItems = rawChildren.map(buildNavItem);

      const base: NavItem = {
        name: m.name,
        href: m.path || undefined,
        icon: menuIcon(m.icon),
        ...(childNavItems.length ? { children: childNavItems } : {}),
      };

      if (String(m.code || '').toLowerCase().trim() === 'settings') {
        return {
          ...base,
          href: firstSettingsSubmenuPath,
          children: managementLinks.map((l) => ({ name: l.name, href: l.href, icon: l.icon })),
        };
      }

      if (isTasksUpdatesMenu(m)) {
        return {
          ...base,
          children: tasksUpdatesChildren.length > 0 ? tasksUpdatesChildren : base.children,
        };
      }

      return base;
    };

    const topLevel = childrenByParent.get(null) || [];
    return topLevel
      .map((m) => {
        const item = buildNavItem(m);
        // A section with pages under it has a front page (Master Data, Reports,
        // Governance, Grow, Settings). My Approvals has none: its name opens its
        // first page instead (see the link below).
        const indexPath = item.children?.length ? sectionByMenuCode(m.code)?.indexPath : null;
        return indexPath ? { ...item, indexPath } : item;
      })
      .map((item) => {
        // A parent with children points at its first child, even when it has a
        // path of its own: My Approvals keeps its pre-dropdown '/approvals', which
        // is not where a role holding only Probation Confirmation belongs.
        const firstChildHref = item.children?.[0]?.href;
        return firstChildHref ? { ...item, href: firstChildHref } : item;
      });
  })();

  const activeTopLevel = (() => {
    let bestItem: NavItem | null = null;
    let bestScore = 0;
    for (const item of navigation) {
      const score = getNavItemActiveScore(item);
      if (score > bestScore) {
        bestScore = score;
        bestItem = item;
      }
    }
    return bestItem;
  })();

  // The pages of a section, in menu order. A row with pages of its own is a heading.
  const pagesOf = (item: NavItem): SectionPage[] =>
    (item.children ?? []).flatMap((child) => {
      if (child.children?.length) return pagesOf(child);
      return child.href
        ? [{ name: child.name, to: child.href, icon: child.icon, current: isHrefActive(child.href) }]
        : [];
    });

  // A section with no front page (My Approvals) is reached from the top bar by its first
  // page only, so its pages are handed to the page on screen, which shows them as tabs
  // under its title (SectionPages). A section with a front page needs no such tabs: its
  // front page is that list.
  const pagesOfOpenSection: OpenSectionPages | null = activeTopLevel && !activeTopLevel.indexPath
    ? { section: activeTopLevel.name, pages: pagesOf(activeTopLevel) }
    : null;

  // The top bar holds four things for everyone: the logo, the menus, Search and the
  // profile button. A person who has Messages in the selected project gets a fifth, the
  // round Messages link between Search and the profile button (see MessagesLink; it is
  // placed by one line below, so it can be moved behind the profile button by moving
  // that line). Nothing else belongs here.
  // What gives way, and below which width, so the menus stay on one row with room around
  // them: the icon beside each menu's name never gives way on a desktop, because people
  // find a menu by it. With six or seven menus the word "Search" goes first (the round
  // button stays), then some padding, and on the narrowest desktop the word
  // "ChannelPlex". The fewer menus a person has, the longer each is kept. The phone menu
  // is untouched.
  // Measured in a browser with seven menus at 1024, 1280, 1366, 1440, 1536, 1680 and
  // 1920px: one row at each, with the Messages link and without it. With the link the
  // fit at 1024px is close (about 6px to spare, on a Mac's own font): the link sits 8px
  // nearer Search on the narrowest desktop to make that room (see MessagesLink). If the
  // menus ever do not fit, the last one moves to a second row; nothing is cut off.
  const fit = navigation.length > 5
    ? {
        searchWord: 'hidden sm:inline lg:hidden min-[1366px]:inline',
        searchBox: 'w-9 justify-center px-0 sm:w-40 sm:justify-start sm:px-3 lg:w-9 lg:justify-center lg:px-0 min-[1366px]:w-44 min-[1366px]:justify-start min-[1366px]:px-3 min-[1536px]:w-56',
        navPad: 'lg:px-1.5 xl:px-3',
        wordmark: 'lg:hidden xl:block',
      }
    : {
        searchWord: 'hidden sm:inline lg:hidden xl:inline',
        searchBox: 'w-9 justify-center px-0 sm:w-40 sm:justify-start sm:px-3 lg:w-9 lg:justify-center lg:px-0 xl:w-44 xl:justify-start xl:px-3 min-[1440px]:w-56',
        navPad: navigation.length > 4 ? 'lg:px-2 xl:px-3' : '',
        wordmark: '',
      };

  // The line under the menu a person is in. It is drawn ON the rule that closes the top
  // bar, not floating above it: the menus fill the bar's height, and the line drops by
  // the bar's bottom padding (py-2.5, 10px) plus the 1px rule, so the two coincide.
  const ACTIVE_MENU_LINE =
    "lg:after:absolute lg:after:inset-x-0 lg:after:-bottom-[11px] lg:after:h-0.5 lg:after:bg-primary-600 lg:after:content-['']";

  const NavigationLinks = ({ onNavigate, isMobile }: { onNavigate?: () => void; isMobile?: boolean }) => {
    return (
      // Desktop: if the menus still do not fit, the last ones move to a second row
      // instead of running under the name.
      <div className="flex flex-col lg:flex-row lg:flex-wrap lg:items-stretch lg:gap-x-1">
        {navigation.map((item) => {
          const Icon = item.icon;
          const active = activeTopLevel === item;
          const hasChildren = item.children && item.children.length > 0;

          // Phone: a section's name opens the list of its pages in place.
          if (isMobile && hasChildren) {
            const isOpen = openPhoneSection === item.name;
            return (
              <div key={item.name} className="relative">
                <button
                  onClick={() => setOpenPhoneSection(isOpen ? null : item.name)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    active
                      ? 'text-primary-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {item.name}
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setOpenPhoneSection(null)}
                    />
                    <div className="w-full bg-white border border-gray-100 rounded-lg shadow-lg py-2 max-h-96 overflow-y-auto">
                      {item.indexPath && (
                        <Link
                          to={item.indexPath}
                          onClick={() => {
                            setOpenPhoneSection(null);
                            onNavigate?.();
                            setIsMobileMenuOpen(false);
                          }}
                          className={`flex items-center gap-3 px-4 py-2.5 mb-1 border-b border-gray-100 text-sm font-medium transition-colors ${
                            normalisePath(location.pathname) === item.indexPath
                              ? 'bg-primary-50 text-primary-700'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <LayoutGrid className="h-4 w-4" />
                          Everything in {item.name}
                        </Link>
                      )}
                      {item.children!.map((child) => {
                        const ChildIcon = child.icon;
                        const childHref = child.href || child.children?.[0]?.href || '#';
                        const childActive = childHref !== '#' && isHrefActive(childHref);
                        const childHasChildren = child.children && child.children.length > 0;

                        if (!childHasChildren) {
                          return (
                            <Link
                              key={child.name}
                              to={childHref}
                              onClick={() => {
                                setOpenPhoneSection(null);
                                onNavigate?.();
                                setIsMobileMenuOpen(false);
                              }}
                              className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                                childActive
                                  ? 'bg-primary-50 text-primary-700'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <ChildIcon className="h-4 w-4" />
                              {child.name}
                            </Link>
                          );
                        }

                        return (
                          <div key={child.name} className="border-t border-gray-100 first:border-t-0">
                            <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              {child.name}
                            </div>
                            {child.children?.map((leaf) => {
                              const LeafIcon = leaf.icon;
                              const leafHref = leaf.href || '#';
                              const leafActive = leafHref !== '#' && isHrefActive(leafHref);
                              return (
                                <Link
                                  key={leaf.name}
                                  to={leafHref}
                                  onClick={() => {
                                    setOpenPhoneSection(null);
                                    onNavigate?.();
                                    setIsMobileMenuOpen(false);
                                  }}
                                  className={`flex items-center gap-3 px-6 py-2 text-sm font-medium transition-colors ${
                                    leafActive
                                      ? 'bg-primary-50 text-primary-700'
                                      : 'text-gray-700 hover:bg-gray-50'
                                  }`}
                                >
                                  <LeafIcon className="h-4 w-4" />
                                  {leaf.name}
                                </Link>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          }

          // Desktop: every name is one link and none opens a list, on hover, on a click
          // or from the keyboard. A section goes to its front page, which is where a
          // person picks a page. A section with no front page (My Approvals) goes to its
          // first page, and its pages are listed under the trail. In the phone menu only
          // a name with no pages under it (Dashboard) is drawn this way.
          return (
            <Link
              key={item.name}
              to={item.indexPath ?? item.href ?? pagesOf(item)[0]?.to ?? '#'}
              onClick={() => {
                onNavigate?.();
                setIsMobileMenuOpen(false);
              }}
              aria-current={active ? 'page' : undefined}
              className={`group flex items-center gap-2 px-3 ${fit.navPad} py-2 text-sm transition-colors lg:relative ${
                active
                  ? `font-semibold text-primary-600 ${ACTIVE_MENU_LINE}`
                  : 'font-medium text-gray-600 hover:text-gray-900'
              }`}
            >
              {/* The icon stays, a shade quieter than the name, so seven of them in a row
                  read as one calm strip and the menu a person is in stands out. */}
              <Icon className={`h-4 w-4 flex-shrink-0 ${active ? '' : 'text-gray-400 group-hover:text-gray-600'}`} />
              {item.name}
            </Link>
          );
        })}
      </div>
    );
  };

  // The logo and the word "ChannelPlex" are the way home, for everyone: the Dashboard for
  // the people who hold it, and for everyone else the first page they hold, which is
  // where signing in or opening "/" takes them (one rule, navigation/menuTree homePath).
  // Plain until the menu has arrived, because until then nobody knows where home is.
  // The same box sits on the top bar and in the phone menu; the negative margin pays
  // back its padding, so the brand does not move and only the hover shows the box.
  // Either one closes the phone menu: the top bar's logo stays on screen above the open
  // menu, and going home behind a menu that stays open would look like nothing happened.
  const home = homePath(menuItems);
  const closePhoneMenu = () => setIsMobileMenuOpen(false);
  const brandLinkClass =
    '-mx-1.5 -my-1 flex w-fit items-center gap-2.5 rounded-lg px-1.5 py-1 transition-colors hover:bg-gray-900/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';
  const brandLinkProps = {
    'aria-label': 'ChannelPlex, go home', title: 'Home', className: brandLinkClass, onClick: closePhoneMenu,
  } as const;
  const brandMark = (
    <>
      <Logo size="sm" logoUrl={projectLogoSrc} />
      <span className={`hidden sm:block text-lg font-semibold leading-tight text-gray-900 ${fit.wordmark}`}>ChannelPlex</span>
    </>
  );

  // The person, for the profile button: their name with a repeated word dropped
  // ("Sundeep Holani Holani" → "Sundeep Holani"), two initials, and their role.
  const personName = (() => {
    const raw = user?.fullName || user?.userName || user?.email || '';
    const deduped: string[] = [];
    for (const w of raw.trim().split(/\s+/)) {
      if (deduped.length === 0 || deduped[deduped.length - 1].toLowerCase() !== w.toLowerCase()) deduped.push(w);
    }
    return deduped.join(' ');
  })();
  const personInitials = (() => {
    const parts = personName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return personName.slice(0, 2).toUpperCase();
  })();
  const personRole = isSuperAdmin
    ? 'Super Admin'
    : userRoleNames.length > 0
      ? userRoleNames.join(', ')
      : isAdmin
        ? 'Admin'
        : '—';

  // People with the project switcher have always been told when no project is picked;
  // everyone else sees their project's name once it is known, and nothing before that.
  const projectLabel = selectedProjectName || user?.accessibleProjects?.[0]?.projectName || projectDisplayTitle
    || (showProjectSwitcher ? 'No Project Selected' : null);
  // The trail cannot be drawn until the menu arrives. Its row is held open meanwhile,
  // so the page does not jump down a line on every load.
  const isTrailPending = hasRoleAccess === null;
  // The menu has not arrived yet and there is none to show: the top bar draws
  // placeholders where the menus will be. An answer that holds no menu ends it too.
  const isMenuPending = hasRoleAccess === null && !menuAnswered && navigation.length === 0;
  const showsTrailRow = trail.length > 0 || isTrailPending;

  return (
    <div className={`min-h-screen ${canvasClass(location.pathname)}`}>
      {/* Top Navigation */}
      {/* Frosted glass: the page shows through, blurred, as it scrolls under the bar.
          The glass is a layer behind the bar's content (see .glass-bar in index.css). */}
      <header className="glass-bar sticky top-0 z-30 border-b border-gray-200/70">
        <div className="w-full px-6 py-2.5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                onClick={() => setIsMobileMenuOpen((prev) => !prev)}
                aria-label="Toggle navigation menu"
              >
                {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
              {home ? (
                <Link to={home} {...brandLinkProps}>
                  {brandMark}
                </Link>
              ) : (
                <div className="flex items-center gap-2.5">{brandMark}</div>
              )}
            </div>

            {/* Stretched to the full height of the bar, so each menu's bottom edge is the
                bar's and the line under the open menu can sit on the bar's own rule. */}
            <div className="hidden lg:flex flex-1 items-stretch self-stretch gap-6 min-w-0">
              {isMenuPending ? (
                // Where the menus will be, for the moment before they arrive, so the bar
                // is not empty. Shapes only: nothing here is a link or can be read out.
                <div className="flex items-center gap-7 px-3" aria-hidden="true">
                  {MENU_PLACEHOLDER_WIDTHS.map((width, i) => (
                    <span key={i} className={`cp-shimmer h-4 rounded-full ${width}`} />
                  ))}
                </div>
              ) : (
                <NavigationLinks />
              )}
            </div>

            {/* Search: people, stores, pages and the rest. Also opened by "/" anywhere
                outside a text field. Shown at every width, phone included. */}
            <button
              ref={searchButtonRef}
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              title="Search (press /)"
              // Drawn as a search field, because that is what it opens: white, rounded, the
              // word in the light grey of a placeholder and the key that also opens it. On a
              // narrow desktop with a full menu only the round icon is left (see `fit`).
              // ml-auto: on a phone the menus are not in the bar, and the bar would spread
              // what is left, putting Search in the middle with the Messages link after
              // it. This keeps the two together at the right. On a desktop the menus take
              // all the spare room, so it changes nothing there.
              className={`ml-auto flex h-9 flex-shrink-0 items-center gap-2 rounded-full border border-gray-200 bg-white text-sm text-gray-400 shadow-sm transition-colors hover:border-primary-300 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${fit.searchBox}`}
            >
              <Search className="h-4 w-4 flex-shrink-0 text-gray-500" />
              <span className={`flex-1 text-left ${fit.searchWord}`}>Search</span>
              <kbd className={`rounded border border-gray-200 bg-gray-50 px-1.5 text-[11px] font-medium leading-5 text-gray-500 ${fit.searchWord}`} aria-hidden="true">/</kbd>
            </button>

            {/* Messages: the person's own chats and groups, with how much is unread. It
                draws itself only for a person the server says has Messages in this
                project. This one line places it; move the line to move the link. */}
            <MessagesLink projectId={selectedProjectId} userKey={visitKey} />

            {/* The person: one round button. Their name, their role and Sign out are
                behind it, so the bar does not carry them on every page. */}
            {user ? (
              <div className="relative hidden lg:block flex-shrink-0" ref={profileRef}>
                <button
                  ref={profileButtonRef}
                  type="button"
                  onClick={() => setProfileOpen((open) => !open)}
                  aria-haspopup="menu"
                  aria-expanded={profileOpen}
                  aria-label={`${personName}: profile and sign out`}
                  title={personName}
                  className="flex items-center gap-1 rounded-full p-0.5 pr-1.5 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-sm font-bold uppercase text-primary-700">
                    {personInitials}
                  </span>
                  <ChevronDown className={`h-3.5 w-3.5 flex-shrink-0 text-gray-400 transition-transform ${profileOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>
                {profileOpen && (
                  <div role="menu" aria-label="Profile" className="glass-panel absolute right-0 top-full z-20 mt-2 w-64 rounded-2xl p-2">
                    <div className="flex items-center gap-3 px-2 py-2">
                      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold uppercase text-primary-700" aria-hidden="true">
                        {personInitials}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-900" title={personName}>{personName}</p>
                        {/* A long list of roles wraps to two lines; the whole list is on hover. */}
                        <p className="line-clamp-2 text-xs text-gray-500" title={personRole}>{personRole}</p>
                      </div>
                    </div>
                    <div className="my-1 border-t border-gray-100" />
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setProfileOpen(false);
                        openLogoutDialog();
                      }}
                      className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={openLogoutDialog}
                className="hidden lg:flex items-center p-2 text-gray-400 hover:text-red-600 rounded-md transition-colors"
                title="Sign out"
              >
                <LogOut className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Second row, for everyone, kept quiet: the project and then the trail, on one
            line from the left. On a desktop it is as tall as the bar above it (3.75rem:
            the logo's 2.5rem and the bar's py-2.5 twice), so the two read as one header. For the people who have always been able to switch project
            (admin, super admin, multi-project external user) the project's name IS the
            switcher: it opens the list of projects. For everyone else the name goes home,
            as the logo does (plain text until the menu says where home is). Below lg only
            the trail shows: the phone menu holds the project picker. */}
        {(projectLabel || showsTrailRow) && <div className={`${showsTrailRow ? 'flex' : 'hidden lg:flex'} min-h-8 lg:min-h-[3.75rem] items-center gap-2 border-t border-gray-200/60 px-6 py-1.5`}>
          {showProjectSwitcher && projects.length >= 1 ? (
            <div className="relative hidden lg:block flex-shrink-0" ref={projectDropdownRef}>
              <button
                type="button"
                onClick={() => setProjectDropdownOpen((prev) => !prev)}
                aria-expanded={projectDropdownOpen}
                aria-label={`Switch project. Now in ${projectLabel}`}
                title="Switch project"
                className="flex max-w-[26rem] items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 py-1 pl-2.5 pr-2 text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <FolderOpen className="h-4 w-4 text-primary-500 flex-shrink-0" />
                <span className="truncate">{projectLabel}</span>
                <ChevronDown className={`h-3.5 w-3.5 text-gray-400 flex-shrink-0 transition-transform ${projectDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {projectDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setProjectDropdownOpen(false)} />
                  <div className="glass-panel absolute left-0 top-full mt-1 z-20 w-80 rounded-2xl py-1">
                    <div className="px-3 py-2.5 border-b border-gray-200/60">
                      <input
                        type="text"
                        placeholder="Search projects..."
                        value={projectSearch}
                        onChange={(e) => setProjectSearch(e.target.value)}
                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                        autoFocus
                      />
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {filteredProjects.map((p) => (
                        <button
                          key={p.projectId}
                          onClick={() => handleGlobalProjectSelect(p.projectId)}
                          className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                            selectedProjectId === p.projectId
                              ? 'bg-primary-50 text-primary-700 font-semibold border-l-2 border-primary-500'
                              : 'text-gray-700 hover:bg-gray-900/5'
                          }`}
                        >
                          <div className="font-medium truncate">{p.projectName}</div>
                          {p.projectCode && <div className="text-xs text-gray-400 mt-0.5">{p.projectCode}</div>}
                        </button>
                      ))}
                      {filteredProjects.length === 0 && (
                        <div className="px-3 py-4 text-center text-sm text-gray-400">No projects found</div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : projectLabel && home ? (
            // For everyone else the name is the trail's first step and goes home, like the
            // logo above it, drawn like the trail's other links. The switcher above stays
            // the switcher. The hover keeps the whole name, for a name cut short.
            <Link
              to={home}
              aria-label={`${projectLabel}, go home`}
              title={`Home · ${projectLabel}`}
              className="hidden lg:block max-w-[26rem] flex-shrink-0 truncate rounded text-sm font-medium text-gray-700 transition-colors hover:text-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              {projectLabel}
            </Link>
          ) : projectLabel ? (
            <span className="hidden lg:block max-w-[26rem] flex-shrink-0 truncate text-sm font-medium text-gray-700" title={projectLabel}>
              {projectLabel}
            </span>
          ) : null}
          {projectLabel && trail.length > 0 && (
            <ChevronRight className="hidden lg:block h-3.5 w-3.5 flex-shrink-0 text-gray-300" aria-hidden="true" />
          )}
          <Trail items={trail} />
        </div>}

        {/* Mobile navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <div className="w-full px-3 py-3 sm:px-4 sm:py-4 space-y-4">
              {(() => {
                const phoneBrand = (
                  <>
                    <Logo size="sm" logoUrl={projectLogoSrc} />
                    <div className="flex flex-col">
                      <span className="text-lg font-semibold text-gray-900">ChannelPlex</span>
                      <span className="text-[0.55rem] uppercase tracking-[0.35em] text-primary-500">Workforce Intelligence</span>
                    </div>
                  </>
                );
                // Home, as on the top bar; the menu closes behind it, like every link in it.
                return home ? (
                  <Link to={home} {...brandLinkProps}>
                    {phoneBrand}
                  </Link>
                ) : (
                  <div className="flex items-center gap-2.5">{phoneBrand}</div>
                );
              })()}
              <NavigationLinks onNavigate={() => setIsMobileMenuOpen(false)} isMobile />

              {/* Mobile Project Selector (admin, super admin, multi-project external user) */}
              {showProjectSwitcher && (
                <div className="pt-3 border-t border-gray-200">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 px-1">Project</label>
                  {projects.length >= 1 ? (
                    <select
                      value={selectedProjectId || ''}
                      onChange={(e) => {
                        const id = Number(e.target.value);
                        if (id) handleGlobalProjectSelect(id);
                      }}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    >
                      <option value="">Select Project</option>
                      {projects.map((p) => (
                        <option key={p.projectId} value={p.projectId}>
                          {p.projectName}{p.projectCode ? ` (${p.projectCode})` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="block text-sm font-medium text-gray-700 px-1">
                      {selectedProjectName || user?.accessibleProjects?.[0]?.projectName || projectDisplayTitle || 'No Project Selected'}
                    </span>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-2 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    openLogoutDialog();
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}

        {/* The thin line that runs along the bar's bottom edge while a page arrives.
            Kept LAST in the header, and out of its flow, so the rows above are the
            header's rows still. */}
        <TopProgress className="absolute inset-x-0 top-full" />
      </header>

      {/* Main Content */}
      {/* No background here (the canvas shows through), and the fade is opacity only. */}
      <main className={`w-full px-6 pt-4 pb-6 ${pageInClass}`}>
        <SectionPagesContext.Provider value={pagesOfOpenSection}>
          {children}
        </SectionPagesContext.Provider>
      </main>

      {searchOpen && (
        <PageSearch onClose={closeSearch} items={menuItems} rules={menuRules} userKey={visitKey} />
      )}

      {showLogoutConfirm && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-[18px] backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-[12px] bg-white p-[18px] shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Sign out?</h3>
            <p className="mt-[18px] text-sm text-gray-600">You’ll need to sign in again to access the deployment workspace.</p>
            <div className="mt-[18px] flex flex-col gap-[18px] sm:flex-row sm:justify-end">
              <button
                onClick={closeLogoutDialog}
                className="w-full rounded-lg border border-gray-300 px-[18px] py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 sm:w-auto"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="w-full rounded-lg bg-red-600 px-[18px] py-2 text-sm font-medium text-white hover:bg-red-700 sm:w-auto"
              >
                Log out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
